import { DropdownIcon } from '@/assets/images';
import { AppIcon, Dropdown, ListTile } from '@/components';
import { resetAlertModal, setAlertModal } from '@/components/AlertModalRoot/alertModalService';
import { activeRightStack, closeSidePanel, pushSidePanel } from '@/app/store';
import { useAmIGroupAdmin } from '@/features/chats/model/groupParticipants';
import { useChatFeature } from '@/features/chats/chatsRegistry';
import { handleGroupMemberAction } from '@/features/chats/sub-features/chat-info/utils/utils';
import { formatUserIdToJid } from '@/features/chats/utils';
import { UserMail } from '@/features/users';
import { useUserDetails } from '@/features/users/model';
import { Username } from '@/features/users/ui/UserName';
import { UserProfile } from '@/features/users/ui/UserProfile';
import { ExternalBadge } from '@/features/invite/ui';
import { useLanguage } from '@/localization/useLanguage';
import { getLocalUserId } from '@/shared/utils';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

const renderConfirmText = (action, userId, t) => {
   const parts = t('common.CONFIRM_ACTION', { action, name: '[[NAME]]' }).split('[[NAME]]');
   return (
      <>
         {parts[0]}
         <Username userId={userId} truncate={false} />
         {parts[1]}
      </>
   );
};

const ParticipantListItem = React.memo(({ groupId, member }) => {
   const { t } = useLanguage();
   const dispatch = useDispatch();
   const myUserId = getLocalUserId();
   const amIAdmin = useAmIGroupAdmin(groupId);
   const isChatInfoEnabled = useChatFeature('chat-info');
   const { isExternal } = useUserDetails(member.userId, ['isExternal']);

   const isSelf = member.userId === myUserId;
   const isMemberAdmin = member.userType === 'o';
   const canManage = !isSelf;

   const memberName = member.userProfile?.nickName || member.userProfile?.name || member.userId;

   const navigateToChat = useCallback(() => {
      dispatch(
         activeRightStack({
            activeStack: 'chats',
            activeChatId: member.userId,
            activeChatJid: formatUserIdToJid(member.userId),
         }),
      );
      dispatch(closeSidePanel());
   }, [dispatch, member.userId]);

   const handleViewInfo = useCallback(() => {
      dispatch(
         pushSidePanel({
            panelId: 'chat-info',
            targetChatId: member.userId,
            targetJid: formatUserIdToJid(member.userId),
         }),
      );
   }, [dispatch, member.userId]);

   const createAction = useCallback(
      ({ title, label, className, action, confirmText, onClick }) => ({
         label: title,
         className,
         formatter: () => {
            if (onClick) {
               onClick();
               return;
            }

            const onConfirm = () => {
               if (action === 'message') {
                  navigateToChat();
                  return;
               }
               handleGroupMemberAction({ groupId, userId: member.userId, action, displayName: memberName });
            };

            if (!confirmText) {
               onConfirm();
               return;
            }

            const modalId = `groupMember_${action}_${member.userId}`;
            setAlertModal({
               id: modalId,
               visible: true,
               title,
               description: confirmText,
               yesButton: label || title,
               yesAction: () => {
                  onConfirm();
                  resetAlertModal(modalId);
               },
               noButton: t('common.CANCEL'),
               noAction: () => resetAlertModal(modalId),
               onRequestClose: () => resetAlertModal(modalId),
            });
         },
      }),
      [groupId, member.userId, memberName, t, navigateToChat],
   );

   const adminActions = useMemo(() => {
      if (!canManage) return [];

      const actions = [];
      if (amIAdmin) {
         if (isMemberAdmin) {
            actions.push(
               createAction({
                  title: t('chat.REMOVE_ADMIN'),
                  label: t('chat.REMOVE_ADMIN'),
                  className: 'remove-admin',
                  action: 'removeAdmin',
                  confirmText: renderConfirmText(t('chat.REMOVE_ADMIN').toLowerCase(), member.userId, t),
               }),
            );
         } else {
            actions.push(
               createAction({
                  title: t('chat.MAKE_ADMIN'),
                  label: t('chat.MAKE_ADMIN'),
                  className: 'make-admin',
                  action: 'makeAdmin',
                  confirmText: renderConfirmText(t('chat.MAKE_ADMIN').toLowerCase(), member.userId, t),
               }),
            );
         }

         actions.push(
            createAction({
               title: t('chat.REMOVE_FROM_GROUP'),
               label: t('chat.REMOVE'),
               className: 'remove',
               action: 'removeMember',
               confirmText: renderConfirmText(t('chat.REMOVE').toLowerCase(), member.userId, t),
            }),
         );
      }

      if (isChatInfoEnabled) {
         actions.push(
            createAction({
               title: t('chat.VIEW_INFO'),
               action: 'viewInfo',
               onClick: handleViewInfo,
            }),
         );
      }

      actions.push(
         createAction({
            title: t('chat.MESSAGE'),
            action: 'message',
         }),
      );
      return actions;
   }, [canManage, isMemberAdmin, createAction, t, member.userId, amIAdmin, isChatInfoEnabled, handleViewInfo]);

   return (
      <ListTile
         as="div"
         avatar={<UserProfile userId={member.userId} className="chat-avatar chat-avatar-fallback ct-flexCenter" />}
         title={<Username userId={member.userId} className="chat-name" isLocal={isSelf} />}
         subTitle={<UserMail userId={member.userId} className="user-user-status" />}
         trailing={
            <>
               <ExternalBadge isExternalUser={isExternal} className="external-badge--compact" />
               {isMemberAdmin && (
                  <span className={`group-member-role ${isSelf ? 'self-admin' : ''}`}>{t('chat.ADMIN')}</span>
               )}
               {adminActions.length > 0 && (
                  <Dropdown
                     trigger={<AppIcon icon={<DropdownIcon />} />}
                     items={adminActions}
                     position="bottom"
                     buttonClassName="chat-action-btn chat-dropdown-btn"
                  />
               )}
            </>
         }
         showBack={false}
      />
   );
});
ParticipantListItem.displayName = 'ParticipantListItem';

ParticipantListItem.propTypes = {
   groupId: PropTypes.string.isRequired,
   member: PropTypes.shape({
      userId: PropTypes.string.isRequired,
      userType: PropTypes.string,
      userProfile: PropTypes.shape({
         nickName: PropTypes.string,
         name: PropTypes.string,
      }),
   }).isRequired,
};

export default ParticipantListItem;
